import { useEffect, useRef, useState, useCallback } from "react";
import { trackFrontendEvent } from "../../utils/tracking";

export const WORKSPACE_CHART_DELIVERY_EVENT = "incite:workspace-chart-delivery";

// Bars are not presentation proof. Rendered requires the native canvas data
// update and a container that intersects the viewport. Each status carries
// wall-clock times so a receipt can be joined with server stage timings (for
// example the answer's response_chart_broadcast_at) for the same answer.
export const useChartDeliveryReceipt = ({
  enabled, threadUuid, messageId, sourceMessageId, tickerId, scale,
  destination, moduleId = null, loaded = false, unavailable = false,
  answerMessageId = null, chartIndex = null, chartCount = null, cacheState = null,
}) => {
  const key = JSON.stringify([threadUuid, messageId, tickerId, scale, destination, moduleId]);
  const containerRef = useRef(null);
  const [paintedKey, setPaintedKey] = useState(null);
  const [visibleKey, setVisibleKey] = useState(null);
  const sentRef = useRef({ key: null, states: new Set(), started: 0, assignedAt: 0 });
  const onRendered = useCallback((painted) => {
    if (painted) setPaintedKey(key);
  }, [key]);
  const painted = paintedKey === key;
  const status = unavailable ? "unavailable" :
    painted && visibleKey === key && loaded ? "rendered" : loaded ? "loaded" : "loading";

  useEffect(() => {
    if (!enabled || !containerRef.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setVisibleKey(entry.isIntersecting ? key : null);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [enabled, key]);

  useEffect(() => {
    if (!enabled) return;
    if (sentRef.current.key !== key) {
      sentRef.current = { key, states: new Set(), started: performance.now(), assignedAt: Date.now() };
    }
    const receipt = sentRef.current;
    for (const next of ["assigned", status]) {
      if (receipt.states.has(next)) continue;
      receipt.states.add(next);
      const detail = {
        thread_uuid: threadUuid, response_message_id: messageId,
        answer_message_id: answerMessageId ?? messageId,
        source_message_id: sourceMessageId, ticker_id: tickerId, scale,
        destination, module_id: moduleId, status: next,
        chart_index: chartIndex, chart_count: chartCount, cache: cacheState,
        elapsed_ms: Math.round(performance.now() - receipt.started),
        assigned_at_ms: receipt.assignedAt,
        observed_at_ms: Date.now(),
      };
      trackFrontendEvent("workspace_response_chart_delivery", detail);
      if (typeof window !== "undefined" && typeof window.CustomEvent === "function") {
        window.dispatchEvent(new CustomEvent(WORKSPACE_CHART_DELIVERY_EVENT, { detail }));
      }
    }
  }, [answerMessageId, cacheState, chartCount, chartIndex, destination, enabled, key, messageId, moduleId, scale, sourceMessageId, status, threadUuid, tickerId]);

  return { containerRef, painted, onRendered, status: enabled ? status : undefined };
};
